import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

// Firebase
import { db, auth } from '../../../firebase/config';
import { 
    collection, 
    query, 
    getDocs, 
    doc, 
    setDoc,
    collectionGroup, 
    serverTimestamp,
    where
} from 'firebase/firestore';

// IMPORTAMOS LA UTILIDAD DE BORRADO DE REMESA CENTRALIZADA
import { deleteRemesaAumentosFull } from '../../../firebase/firebaseUtils';

// Componentes UI
import ModalConfirmacion from '../../ui/ModalConfirmacion'; 

/**
 * Helpers para formatear de forma segura valores de celdas de Excel
 */
const formatExcelValue = (val) => {
    if (val === undefined || val === null || val === '') return '';
    
    // Si la librería xlsx parseó la celda como objeto Date nativo
    if (val instanceof Date) {
        const m = String(val.getMonth() + 1).padStart(2, '0');
        const y = val.getFullYear();
        return `${m}/${y}`;
    }

    const str = String(val).trim();
    // Soporte para strings con formato MM/YYYY, M/YYYY, MM-YYYY, etc.
    const dateRegex = /^(\d{1,2})[/-](\d{4})$/;
    const match = str.match(dateRegex);
    if (match) {
        const m = match[1].padStart(2, '0');
        const y = match[2];
        return `${m}/${y}`;
    }
    return str;
};

const parsePercent = (val) => {
    if (val === undefined || val === null || val === '') return 0;
    if (typeof val === 'number') return val;
    let clean = String(val).replace(',', '.').replace(/[^\d.]/g, '');
    const num = parseFloat(clean);
    return isNaN(num) ? 0 : num;
};

const generarNumeroEndoso = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `END-${result}`; 
};

// Helper para dividir un array en grupos de tamaño N
const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
};

/**
 * ImportarAumentosArtPage - Importador masivo de aumentos de ART con matching,
 * previsualización de datos antes de confirmar y almacenamiento transaccional en lote por remesa.
 * Optimizado con carga segmentada bajo demanda (Lazy Loading + Query Batching).
 */
const ImportarAumentosArtPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('importar');
    const [file, setFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState({ success: 0, errors: 0 });
    const fileInputRef = useRef(null);

    // Caché local segmentada (Solo guardamos los contratos involucrados en el Excel actual)
    const [contratosList, setContratosList] = useState([]);
    const [loadingCaches, setLoadingCaches] = useState(false); // Inicializado en FALSE para evitar parpadeos inútiles

    // Estados para la vista previa interactiva antes de importar
    const [previewRows, setPreviewRows] = useState([]);
    const [showPreview, setShowPreview] = useState(false);

    const [remesas, setRemesas] = useState([]);
    const [remesaToDelete, setRemesaToDelete] = useState(null);
    const [loadingRemesas, setLoadingRemesas] = useState(false);

    // --- Estados de Paginación del Historial ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // --- 1. SINK OPTIMIZADO: Busca ÚNICAMENTE los números de contrato que están en el Excel ---
    const fetchContratosCacheForNumbers = async (contratosANecesitar) => {
        if (contratosANecesitar.length === 0) return [];
        setLoadingCaches(true);
        addLog(`[SISTEMA] Buscando ${contratosANecesitar.length} contratos únicos en la base de datos de ART...`, "warning");
        
        try {
            // Dividimos nuestra lista de contratos requeridos en grupos de a 30 por límites de Firestore
            const chunks = chunkArray(contratosANecesitar, 30);
            
            // Ejecutamos consultas paralelas para máxima performance
            const promises = chunks.map(async (chunk) => {
                const qContratos = query(
                    collectionGroup(db, 'contratos'),
                    where('nContrato', 'in', chunk)
                );
                const snapContratos = await getDocs(qContratos);
                return snapContratos.docs.map(d => ({
                    id: d.id,
                    path: d.ref.path,
                    ...d.data()
                }));
            });

            const results = await Promise.all(promises);
            const mapped = results.flat(); // Aplanamos los resultados

            setContratosList(mapped);
            addLog(`[SISTEMA] Sincronización exitosa. Se encontraron ${mapped.length} de los ${contratosANecesitar.length} contratos requeridos.`, "success");
            return mapped;
        } catch (error) {
            console.error("Error cargando cachés de ART segmentados:", error);
            addLog("Error crítico al sincronizar contratos de ART de la base de datos.", "error");
            return [];
        } finally {
            setLoadingCaches(false);
        }
    };

    // --- 2. CARGA DEL HISTORIAL DE REMESAS CON LÍMITE TEMPORAL DE 3 MESES ---
    const fetchRemesas = async () => {
        setLoadingRemesas(true);
        try {
            const snap = await getDocs(collection(db, 'public/data/remesas_aumentos'));
            
            // Calculamos el límite estricto de 3 meses atrás
            const limiteMeses = new Date();
            limiteMeses.setMonth(limiteMeses.getMonth() - 3);
            const limitTime = limiteMeses.getTime();

            // Mapeamos y aplicamos el filtro temporal síncronamente en memoria
            const dataFiltrada = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(r => {
                    const remesaDate = r.fecha ? new Date(r.fecha).getTime() : 0;
                    return remesaDate >= limitTime;
                });

            // Ordenamiento por fecha descendente
            dataFiltrada.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
            
            setRemesas(dataFiltrada);
            setCurrentPage(1); 
        } catch (error) {
            console.error("Error al cargar remesas de aumentos:", error);
        } finally {
            setLoadingRemesas(false);
        }
    };

    // EFECTO: Solo cargamos el historial si la pestaña activa es remesas
    useEffect(() => {
        if (activeTab === 'remesas') {
            fetchRemesas();
        }
    }, [activeTab]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setProcessing(false);
        setLogs([]);
        setStats({ success: 0, errors: 0 });
        setPreviewRows([]);
        setShowPreview(false);
        setFile(selectedFile);
        setContratosList([]); // Reseteamos la caché local al cambiar el archivo
    };

    const addLog = (msg, type = 'info') => {
        setLogs(prev => [...prev, { msg, type, time: new Date().toLocaleTimeString() }]);
    };

    const handleCancelPreview = () => {
        setFile(null);
        setPreviewRows([]);
        setShowPreview(false);
        setLogs([]);
        setStats({ success: 0, errors: 0 });
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // --- 3. PROCESAMIENTO SÍNCRONO DEL EXCEL EN MEMORIA (Vista Previa) ---
    const parseExcelToPreview = async () => {
        if (!file) return;
        setProcessing(true);
        setLogs([]);
        setStats({ success: 0, errors: 0 });

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
                const json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);

                if (json.length === 0) {
                    addLog("El archivo seleccionado está vacío o corrupto.", "error");
                    setProcessing(false);
                    return;
                }

                // 1. Extraemos de forma limpia y única todos los números de contrato presentes en el Excel
                const contratosEnExcel = Array.from(new Set(
                    json.map(row => {
                        let nContratoExcel = row.nContrato || row.Contrato || row['N° Contrato'] || '';
                        nContratoExcel = String(nContratoExcel).trim();
                        if (nContratoExcel.endsWith('.0')) nContratoExcel = nContratoExcel.replace('.0', '');
                        return nContratoExcel;
                    }).filter(Boolean)
                ));

                // 2. Buscamos SÓLO esos contratos en Firebase (Ahorro de un 99% de lecturas innecesarias)
                const currentCache = await fetchContratosCacheForNumbers(contratosEnExcel);

                if (currentCache.length === 0) {
                    addLog("Advertencia: No se encontró correspondencia de ninguna póliza en la base de datos.", "warning");
                }

                addLog(`Indexando ${json.length} filas del archivo Excel...`, 'info');

                // Mapear contratos encontrados en memoria para matching O(1) instantáneo
                const contratosMap = {};
                currentCache.forEach(c => {
                    if (c.nContrato) {
                        contratosMap[String(c.nContrato).trim()] = c;
                    }
                });

                let validos = 0;
                let erroneos = 0;
                const rowsProcessed = [];

                json.forEach((row) => {
                    let nContratoExcel = row.nContrato || row.Contrato || row['N° Contrato'] || '';
                    nContratoExcel = String(nContratoExcel).trim();
                    if (nContratoExcel.endsWith('.0')) nContratoExcel = nContratoExcel.replace('.0', '');

                    const mesAumentoStr = formatExcelValue(row.mesAumento || row['Mes Aumento']);
                    const periodoArcaStr = formatExcelValue(row.periodoArca || row['Periodo ARCA'] || row['Periodo Arca']);
                    const alicAct = parsePercent(row.alicuotaActual || row['Alicuota Actual'] || row['Alícuota Actual']);
                    const alicAum = parsePercent(row.alicuotaAumentada || row['Alicuota Aumentada'] || row['Alícuota Aumentada']);

                    if (!nContratoExcel) {
                        erroneos++;
                        return;
                    }

                    const contratoPadre = contratosMap[nContratoExcel];
                    const hasMatch = !!contratoPadre;
                    const hasDates = !!(mesAumentoStr && periodoArcaStr);
                    const isCorrect = hasMatch && hasDates;

                    if (isCorrect) validos++;
                    else erroneos++;

                    const diff = alicAct > 0 ? (((alicAum - alicAct) / alicAct) * 100).toFixed(2) : 0;

                    rowsProcessed.push({
                        nContrato: nContratoExcel,
                        mesAumento: mesAumentoStr,
                        periodoArca: periodoArcaStr,
                        alicuotaActual: alicAct,
                        alicuotaAumentada: alicAum,
                        porcentajeAumento: parseFloat(diff),
                        contratoPadre: contratoPadre || null,
                        isCorrect,
                        statusText: !hasMatch 
                            ? 'Contrato no encontrado' 
                            : !hasDates 
                                ? 'Falta Mes/Periodo' 
                                : 'Listo para importar'
                    });
                });

                setPreviewRows(rowsProcessed);
                setStats({ success: validos, errors: erroneos });
                setShowPreview(true);
                addLog(`Indexación terminada. ${validos} listos para importar. ${erroneos} con errores.`, 'info');

            } catch (fileError) {
                addLog(`Error de lectura: ${fileError.message}`, 'error');
            } finally {
                setProcessing(false);
            }
        };
        reader.readAsBinaryString(file);
    };

    // --- 4. CONFIRMACIÓN Y GUARDADO DE PREVIA EN FIRESTORE ---
    const handleConfirmImport = async () => {
        if (previewRows.length === 0 || processing) return;
        setProcessing(true);
        addLog("Iniciando persistencia masiva en base de datos...", "info");

        try {
            const batchId = new Date().getTime().toString();
            const remesaRef = doc(collection(db, 'public/data/remesas_aumentos'));
            const remesaDocId = remesaRef.id;

            let creadosCount = 0;
            const creadosPaths = [];

            for (const item of previewRows) {
                if (!item.isCorrect) continue;

                try {
                    const contratoPadre = item.contratoPadre;
                    const endosoAleatorio = generarNumeroEndoso();

                    const payload = {
                        numeroEndoso: endosoAleatorio,
                        nContrato: item.nContrato,
                        contratoId: contratoPadre.id,
                        mesAumento: item.mesAumento,
                        periodoArca: item.periodoArca,
                        alicuotaActual: item.alicuotaActual,
                        alicuotaAumentada: item.alicuotaAumentada,
                        porcentajeAumento: item.porcentajeAumento,
                        estado: 'Pendiente',
                        canal: contratoPadre.canal || '',
                        canalNombre: contratoPadre.canalNombre || '',
                        canalNombreId: contratoPadre.canalNombreId || '',
                        ciiu: contratoPadre.ciiu || '',
                        clientId: contratoPadre.clienteId || '',
                        clienteId: contratoPadre.clienteId || '',
                        clienteNombre: contratoPadre.clienteNombre || 'S/D',
                        clienteApellido: contratoPadre.clienteApellido || 'S/D',
                        clienteCuit: contratoPadre.clienteCuit || '',
                        clienteDni: contratoPadre.clienteDni || '',
                        compania: contratoPadre.compania || '',
                        companiaId: contratoPadre.companiaId || '',
                        subcanalNombre: contratoPadre.subcanalNombre || '',
                        subcanalNombreId: contratoPadre.subcanalNombreId || '',
                        fechaCarga: new Date().toISOString().split('T')[0],
                        batchId: batchId,
                        remesaDocId: remesaDocId,
                        fechaCreacion: serverTimestamp(),
                        ultimaModificacion: serverTimestamp()
                    };

                    const parentPath = contratoPadre.path; 
                    const aumentosColRef = collection(db, `${parentPath}/aumentos_art`);

                    const newDocRef = doc(aumentosColRef);
                    await setDoc(newDocRef, payload);
                    
                    creadosPaths.push(newDocRef.path);
                    creadosCount++;

                } catch (rowError) {
                    addLog(`Error al guardar contrato #${item.nContrato}: ${rowError.message}`, "error");
                }
            }

            if (creadosCount > 0) {
                await setDoc(remesaRef, {
                    id: remesaDocId,
                    fecha: new Date().toISOString(),
                    compania: 'ART AUMENTOS',
                    archivo: file.name,
                    registros: creadosCount,
                    batchId: batchId,
                    documentosCreados: creadosPaths
                });

                addLog(`[ÉXITO] Se importaron ${creadosCount} aumentos correctamente. Remesa registrada.`, 'success');
            } else {
                addLog("No se pudo persistir ningún registro en la base de datos.", "error");
            }

            setFile(null);
            setPreviewRows([]);
            setShowPreview(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
            setContratosList([]); // Limpiamos la caché de memoria al terminar

        } catch (error) {
            console.error("Error crítico durante la importación masiva:", error);
            addLog(`Error crítico de guardado: ${error.message}`, 'error');
        } finally {
            setProcessing(false);
        }
    };

    // --- 5. GESTIÓN Y ELIMINACIÓN DE REMESAS USANDO LA NUEVA UTILIDAD ---
    const handleDeleteRemesa = async () => {
        if (!remesaToDelete) return;
        setProcessing(true);
        try {
            addLog(`Iniciando reversión del lote de aumentos registrados...`, 'warning');

            // LLAMADA A LA FUNCIÓN DE BORRADO PROFUNDO CENTRALIZADA AUTÓNOMA
            await deleteRemesaAumentosFull(remesaToDelete.id);

            addLog(`Lote de remesa "${remesaToDelete.archivo}" eliminado y revertido con éxito.`, 'success');
            setRemesaToDelete(null);
            fetchRemesas();
        } catch (error) {
            console.error("Error al eliminar la remesa de aumentos:", error);
            addLog(`Error al revertir la remesa: ${error.message}`, 'error');
        } finally {
            setProcessing(false);
        }
    };

    // --- Lógica de Paginación mediante useMemo ---
    const totalPages = Math.ceil(remesas.length / itemsPerPage);
    const paginatedRemesas = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return remesas.slice(start, start + itemsPerPage);
    }, [remesas, currentPage]);

    return (
        <div className="min-h-screen bg-slate-50 p-8 md:p-12 font-sans text-slate-800 text-left transition-all duration-300">
            <div className="max-w-[95%] mx-auto">
                
                {/* CABECERA */}
                <div className="flex justify-between items-center mb-8 text-left select-none">
                    <div className="text-left">
                        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight text-left uppercase">Importador de Aumentos ART</h1>
                        <p className="text-slate-500 mt-1.5 text-sm text-left font-medium">Carga masiva de incrementos con previsualización inteligente y rollback en lote.</p>
                    </div>
                    <button onClick={() => navigate('/CargaCotizacionArtPage')} className="px-5 py-2.5 text-slate-700 bg-white border border-slate-200 rounded-xl font-bold hover:text-orange-600 shadow-sm transition-all uppercase text-[10px] tracking-widest active:scale-95 transform">Volver al Cpanel</button>
                </div>

                {/* TABS DE CONTROL */}
                <div className="flex space-x-1 bg-slate-200 p-1 rounded-xl w-fit mb-6 text-left select-none">
                    <button onClick={() => setActiveTab('importar')} className={`px-6 py-2 rounded-lg text-xs font-black uppercase transition-all ${activeTab === 'importar' ? 'bg-white text-orange-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Importar Aumentos</button>
                    <button onClick={() => setActiveTab('remesas')} className={`px-6 py-2 rounded-lg text-xs font-black uppercase transition-all ${activeTab === 'remesas' ? 'bg-white text-orange-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Historial Remesas</button>
                </div>

                {/* VISTA: IMPORTADOR */}
                {activeTab === 'importar' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in text-left">
                        
                        <div className="lg:col-span-1 space-y-6 text-left">
                            {!showPreview ? (
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 text-left select-none">
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-3 text-left">Archivo Excel (.xlsx, .xls)</label>
                                    <div className="relative border-2 border-dashed border-slate-200 rounded-2xl p-8 hover:bg-slate-50 transition cursor-pointer flex flex-col items-center justify-center">
                                        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".xlsx, .xls" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" disabled={processing} />
                                        <svg className="w-8 h-8 text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                                        <p className="text-sm text-slate-500 font-bold tracking-tight text-center">{file ? file.name : "Clic o arrastrar archivo"}</p>
                                    </div>
                                    <button onClick={parseExcelToPreview} disabled={!file || processing || loadingCaches} className={`w-full py-4 mt-6 rounded-xl font-black text-white shadow-lg flex items-center justify-center gap-2 uppercase text-xs tracking-widest transition-all ${!file || processing || loadingCaches ? 'bg-slate-300 shadow-none cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700 shadow-orange-100 active:scale-95 transform'}`}>
                                        {loadingCaches ? 'Buscando Contratos...' : processing ? 'Indexando...' : 'Previsualizar Lote'}
                                    </button>
                                </div>
                            ) : (
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 text-left space-y-4">
                                    <h3 className="text-sm font-black text-slate-700 uppercase tracking-wider border-b pb-2 select-none">Confirmar Remesa</h3>
                                    <div className="flex gap-2">
                                        <button onClick={handleCancelPreview} className="flex-1 py-3.5 border border-slate-300 text-slate-600 font-bold uppercase text-xs tracking-wider rounded-xl hover:bg-slate-50 transition active:scale-95 transform select-none">
                                            Cancelar
                                        </button>
                                        <button onClick={handleConfirmImport} disabled={stats.success === 0 || processing} className="flex-2 py-3.5 px-6 bg-orange-600 text-white font-black uppercase text-xs tracking-wider rounded-xl hover:bg-orange-700 shadow-lg shadow-orange-100 active:scale-95 transform select-none disabled:opacity-50 disabled:cursor-not-allowed">
                                            {processing ? 'Guardando...' : 'Confirmar Lote'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3 text-center font-bold select-none">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 text-emerald-600">
                                    <div className="text-lg font-black">{stats.success}</div>
                                    <div className="text-[8px] uppercase text-center font-black tracking-widest mt-1">Sin Errores</div>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100 text-rose-600">
                                    <div className="text-lg font-black">{stats.errors}</div>
                                    <div className="text-[8px] uppercase text-center font-black tracking-widest mt-1">Con Errores</div>
                                </div>
                            </div>
                        </div>

                        {/* SECCIÓN DERECHA: CONSOLA DE LOGS O TABLA DE PREVISUALIZACIÓN */}
                        <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col h-[600px] text-left relative">
                            {!showPreview ? (
                                <div className="p-6 flex flex-col h-full bg-slate-900 border border-slate-800 font-mono text-xs relative overflow-hidden text-left">
                                    <p className="text-slate-500 font-bold uppercase tracking-widest mb-4 border-b border-slate-800 pb-2 text-[10px] select-none">Consola de Control del Lote</p>
                                    <div className="flex-grow overflow-y-auto space-y-1.5 pr-2 custom-scrollbar text-left font-semibold">
                                        {loadingCaches && (
                                            <div className="text-amber-400 animate-pulse text-left">[SISTEMA] Sincronizando diccionario de contratos de ART de la base de datos...</div>
                                        )}
                                        {logs.length === 0 && !loadingCaches && (
                                            <div className="text-slate-500 italic">Consola vacía. Seleccione un archivo Excel para iniciar la sincronización y previsualización de aumentos.</div>
                                        )}
                                        {logs.map((l, i) => (
                                            <div key={i} className={`flex gap-3 text-left ${l.type === 'error' ? 'text-rose-400 font-bold' : l.type === 'warning' ? 'text-amber-400' : l.type === 'success' ? 'text-emerald-400 font-bold' : 'text-slate-300'}`}>
                                                <span className="opacity-40 tabular-nums">[{l.time}]</span><span>{l.msg}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col h-full animate-fade-in text-left">
                                    <div className="p-4 bg-slate-50 border-b select-none flex justify-between items-center shrink-0">
                                        <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest">Vista previa de registros cargados</h4>
                                        <span className="text-[10px] font-black text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-100 uppercase">Listo para revisión</span>
                                    </div>
                                    <div className="flex-grow overflow-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                                        <table className="w-full text-left text-xs border-collapse">
                                            <thead className="bg-slate-50/50 border-b text-slate-500 uppercase font-black text-[9px] tracking-wider select-none sticky top-0 bg-white z-10">
                                                <tr>
                                                    <th className="px-4 py-3">Contrato N°</th>
                                                    <th className="px-4 py-3">Asegurado / Cía</th>
                                                    <th className="px-4 py-3">Mes / ARCA</th>
                                                    <th className="px-4 py-3 text-right">Alíc. Actual / Aum.</th>
                                                    <th className="px-4 py-3 text-center">Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {previewRows.map((row, idx) => (
                                                    <tr key={idx} className={`hover:bg-slate-50/50 ${!row.isCorrect ? 'bg-rose-50/30' : ''}`}>
                                                        <td className="px-4 py-3 font-bold text-slate-800">#{row.nContrato}</td>
                                                        <td className="px-4 py-3">
                                                            {row.contratoPadre ? (
                                                                <div className="flex flex-col text-left">
                                                                    <span className="font-bold text-slate-700">{row.contratoPadre.clienteNombre}</span>
                                                                    <span className="text-[10px] text-slate-400 font-bold uppercase">{row.contratoPadre.compania}</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-rose-500 font-bold italic select-none">No aplica</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 font-mono font-bold text-slate-600">
                                                            {row.mesAumento} / {row.periodoArca}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-mono">
                                                            <div className="font-bold text-slate-600">{row.alicuotaActual.toFixed(2)}%</div>
                                                            <div className="font-black text-orange-600">{row.alicuotaAumentada.toFixed(2)}% ({row.porcentajeAumento >= 0 ? '+' : ''}{row.porcentajeAumento}%)</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-center select-none">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase border tracking-tight ${
                                                                row.isCorrect 
                                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                                                                    : 'bg-rose-50 text-rose-700 border-rose-100 font-black'
                                                            }`}>
                                                                {row.statusText}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'remesas' && (
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-fade-in flex flex-col justify-between min-h-[500px] text-left">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm border-collapse">
                                <thead className="bg-slate-50 text-slate-500 uppercase font-black text-[9px] tracking-widest border-b select-none">
                                    <tr>
                                        <th className="px-6 py-4">Fecha de Lote (Últimos 3 Meses)</th>
                                        <th className="px-6 py-4">Canal / Cía</th>
                                        <th className="px-6 py-4">Archivo Cargado</th>
                                        <th className="px-6 py-4 text-center">Registros Creados</th>
                                        <th className="px-6 py-4 text-center">Reversión</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {loadingRemesas ? (
                                        <tr><td colSpan="5" className="p-16 text-center text-slate-400 font-bold animate-pulse text-xs uppercase tracking-widest">Cargando histórico de remesas...</td></tr>
                                    ) : remesas.length === 0 ? (
                                        <tr><td colSpan="5" className="p-16 text-center text-slate-400 italic">No se encontraron remesas de aumentos importadas en los últimos 3 meses.</td></tr>
                                    ) : (
                                        paginatedRemesas.map(r => (
                                            <tr key={r.id} className="hover:bg-orange-50/20 transition-colors">
                                                <td className="px-6 py-4 font-mono text-xs text-slate-500">{new Date(r.fecha).toLocaleString('es-AR')}</td>
                                                <td className="px-6 py-4 font-bold text-slate-800">{r.compania || 'ART AUMENTOS'}</td>
                                                <td className="px-6 py-4 text-slate-500 italic select-all">{r.archivo}</td>
                                                <td className="px-6 py-4 text-center font-black text-orange-600 tabular-nums">{r.registros}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <button onClick={() => setRemesaToDelete(r)} className="text-rose-500 hover:text-white border border-rose-100 hover:bg-rose-500 px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all select-none">Revertir Lote</button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Panel de Paginación Inmubtable */}
                        {totalPages > 1 && !loadingRemesas && (
                            <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between select-none shrink-0">
                                <div className="text-xs text-slate-500 font-semibold">
                                    Mostrando <span className="font-bold text-slate-700">{((currentPage - 1) * itemsPerPage) + 1}</span> a{' '}
                                    <span className="font-bold text-slate-700">{Math.min(currentPage * itemsPerPage, remesas.length)}</span> de{' '}
                                    <span className="font-bold text-slate-700">{remesas.length}</span> remesas
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className={`px-3 py-1.5 rounded-lg border text-xs font-black uppercase tracking-wider transition-all ${
                                            currentPage === 1
                                                ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                                                : 'bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 border-slate-200 active:scale-95 transform'
                                        }`}
                                    >
                                        Anterior
                                    </button>
                                    
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            type="button"
                                            onClick={() => setCurrentPage(page)}
                                            className={`w-8 h-8 rounded-lg border text-xs font-black transition-all ${
                                                currentPage === page
                                                    ? 'bg-orange-600 text-white border-orange-600 shadow-sm'
                                                    : 'bg-white text-slate-500 hover:bg-slate-100 border-slate-200'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    ))}

                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className={`px-3 py-1.5 rounded-lg border text-xs font-black uppercase tracking-wider transition-all ${
                                            currentPage === totalPages
                                                ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                                                : 'bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 border-slate-200 active:scale-95 transform'
                                        }`}
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {/* MODAL DE SEGURIDAD PARA ROLLBACK */}
            <ModalConfirmacion 
                show={!!remesaToDelete} 
                onClose={() => setRemesaToDelete(null)} 
                onConfirm={handleDeleteRemesa} 
                title="Revertir Lote de Aumentos" 
                message={`¿Está completamente seguro de eliminar la remesa "${remesaToDelete?.archivo}"? Se borrarán de forma masiva los ${remesaToDelete?.registros} aumentos creados en las fichas de los clientes de forma irreversible.`} 
            />
        </div>
    );
};

export default ImportarAumentosArtPage;